import {
  createUser,
  encode,
  db,
  auth,
  timestamp,
  currentUser,
} from './firebase'
import { CONSULTANT } from 'userTypes'

export * from './search'
export * from './consultants'
export * from './settings'

export const visit = page =>
  db.ref(`users/${currentUser()}/visit/${page}`).set(timestamp)

export const resetPassword = email => auth.sendPasswordResetEmail(email)

export const subscribeToUserRooms = emit => {
  const ref = db.ref(`users/${currentUser()}/rooms`)
  const onValue = ref.on('value', snapshot => emit(snapshot.val() || {}))
  return () => ref.off('value', onValue)
}

export const subscribeToRoomMetadata = roomId => emit => {
  const ref = db.ref(`room-metadata/${roomId}`)
  const onValue = ref.on('value', snapshot => emit(snapshot.val() || {}))
  return () => ref.off('value', onValue)
}

export const sendMessage = (roomId, message) => {
  const msg = {
    message,
    timestamp,
    userId: currentUser(),
  }
  Promise.all([
    db.ref(`room-messages/${roomId}`).push(msg),
    db.ref(`room-metadata/${roomId}/lastMessage`).update(msg),
    db.ref(`room-metadata/${roomId}/members`).transaction(members =>
      Object.keys(members || {}).reduce(
        (result, id) => ({
          ...result,
          [id]:
            id !== currentUser()
              ? Number.isInteger(members[id]) ? members[id] + 1 : 1
              : 0,
        }),
        {}
      )
    ),
  ])
}

export const subscribeToMessages = roomId => emit => {
  const ref = db.ref(`room-messages/${roomId}`)
  const onValue = ref.on('value', snapshot => emit(snapshot.val() || {}))
  return () => ref.off('value', onValue)
}

export const resetNotificationCounter = roomId =>
  db.ref(`room-metadata/${roomId}/members/${currentUser()}`).set(0)

export const saveCustomerContact = data => db.ref(`customer-contact`).push(data)

export const contactConsultant = (id, message) => {
  const roomId = db.ref('room-messages').push().key
  return Promise.all([
    sendMessage(roomId, message),
    db.ref(`room-metadata/${roomId}/members`).set({
      [id]: true,
      [currentUser()]: true,
    }),
    db.ref(`users/${currentUser()}/rooms/${roomId}`).set(true),
    db.ref(`users/${id}/rooms/${roomId}`).set(true),
  ])
}

export const saveConsultant = id =>
  db.ref(`user-saved/${currentUser()}/${id}`).set(true)

export const unsaveConsultant = id =>
  db.ref(`user-saved/${currentUser()}/${id}`).set(null)

export const fetchSavedConsultants = () =>
  db
    .ref(`user-saved/${currentUser()}`)
    .once('value')
    .then(snapshot => snapshot.val())

export const fetchContactedConsultants = () =>
  db
    .ref(`user-contacted/${currentUser()}`)
    .once('value')
    .then(snapshot => snapshot.val())

export const register = (name, email, password) =>
  auth.createUserWithEmailAndPassword(email, password).then(
    ({ uid }) =>
      new Promise((resolve, reject) => {
        createUser({ type: CONSULTANT, name })
          .then(() => fetchUser(uid).then(resolve))
          .catch(reject)
      })
  )

export const logIn = (email, password) =>
  auth
    .signInWithEmailAndPassword(email, password)
    .then(user => fetchUser(user.uid))

export const logOut = () => auth.signOut()

export const checkAuthStatus = () =>
  new Promise((resolve, reject) => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        fetchUser(user.uid)
          .then(user => {
            resolve(user)
            unsubscribe()
          })
          .catch(e => {
            reject(e)
            unsubscribe()
          })
      } else {
        resolve()
        unsubscribe()
      }
    }, reject)
  })

const fetchUser = id =>
  Promise.all([
    db
      .ref(`/users/${id}`)
      .once('value')
      .then(snapshot => snapshot.val()),
    db
      .ref(`/user-info/${id}`)
      .once('value')
      .then(snapshot => snapshot.val()),
  ]).then(([user, info]) => ({
    ...user,
    ...info,
    id,
  }))

export const saveSearch = (query, available) => {
  let search = {}
  if (query) search.query = query
  if (available) search.available = available
  return db.ref(`user-searches/${currentUser()}`).push(search)
}

const mapExperienceToFirebase = ({
  type,
  duration,
  description,
  tags,
  customer,
  employer,
  name,
  degree,
  university,
  roles,
}) => {
  const firebase = {
    timestamp,
    type,
    duration,
    description,
  }

  if (customer && customer.length) firebase.customer = customer
  if (employer && employer.length) firebase.employer = employer
  if (name && name.length) firebase.name = name
  if (degree && degree.length) firebase.degree = degree
  if (university && university.length) firebase.university = university
  if (roles && roles.length)
    firebase.roles = roles.reduce(
      (roles, role) => ({
        ...roles,
        [encode(role)]: true,
      }),
      {}
    )
  if (tags && tags.length)
    firebase.tags = tags.reduce(
      (tags, tag) => ({
        ...tags,
        [encode(tag)]: true,
      }),
      {}
    )

  return firebase
}

export const createExperience = (consultantId, experience) =>
  db
    .ref(`user-experience/${consultantId}`)
    .push(mapExperienceToFirebase(experience))
    .then(({ key }) => key)

export const updateExperience = (consultantId, experienceId, experience) =>
  db
    .ref(`user-experience/${consultantId}/${experienceId}`)
    .set(mapExperienceToFirebase(experience))

export const deleteExperience = (consultantId, experienceId) =>
  db.ref(`user-experience/${consultantId}/${experienceId}/deleted`).set(true)

export const completeOnboarding = () =>
  db.ref(`users/${currentUser()}/newUser`).set(false)



// WEBPACK FOOTER //
// ./src/api/index.js